import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Card, Reveal, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { getServiceLine } from '../data/services';
import { ARTICLES } from '../data/insights';
import { NotFound } from './NotFound';
export function ServiceLinePage() {
  const {
    lineId
  } = useParams<{
    lineId: string;
  }>();
  const line = getServiceLine(lineId ?? '');
  if (!line) return <NotFound data-id="element-1315" data-mp="30mewrqmzx7br" />;
  const related = ARTICLES.filter(article => article.serviceLine === line.id).slice(0, 3);
  return <>
      <section className="surface-dark" data-id="element-1316" data-mp="2rs9xqjxpkpl5">
        <Container className="py-14 sm:py-20" data-id="element-1317" data-mp="2rr8nuisfi950">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-1318" data-mp="3o9whx3br975r">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-1319" data-mp="3obha4malke0g">
              <li data-id="element-1320" data-mp="3jiegyf9qf6ks"><Link to="/" className="hover:text-white" data-id="element-1321" data-mp="3jieiijpdremw">Home</Link></li>
              <li aria-hidden="true" data-id="element-1322" data-mp="2kdtc6p53297n"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1323" data-mp="2kfe5ycjkpo4g" /></li>
              <li data-id="element-1324" data-mp="2pcwgu8h5idf3"><Link to="/services" className="hover:text-white" data-id="element-1325" data-mp="2pdpi4txccapu">Services</Link></li>
              <li aria-hidden="true" data-id="element-1326" data-mp="2ao9wfsco2vh0"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1327" data-mp="2ap1powiskh44" /></li>
              <li aria-current="page" className="text-white/80" data-id="element-1328" data-mp="2amfhcuo2txku">{line.name}</li>
            </ol>
          </nav>
          <Kicker data-id="element-1329" data-mp="21nlfopvw2h0f">{line.short}</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-1330" data-mp="21mk0qabr5adf">
            {line.name}
          </h1>
          <p className="mt-6 max-w-2xl text-[19px] text-white/80" data-id="element-1331" data-mp="26kbuzazvd8ks">{line.promise}</p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-1332" data-mp="1rr8p40vn4j9w">
            <Button to="/contact" size="lg" data-id="element-1333" data-mp="1wpjvg1mq8bnd">Book a discovery call</Button>
            <Button to="/augmented-intelligence" variant="reversed" size="lg" data-id="element-1334" data-mp="3m2x586dlyrvv">Read our method</Button>
          </div>
        </Container>
      </section>

      <Section data-id="element-1335" data-mp="3r1qibxozggvi">
        <div className="grid gap-10 lg:grid-cols-2" data-id="element-1336" data-mp="3r05pqdm8b80a">
          <div data-id="element-1337" data-mp="3pmdzt564no74">
            <Kicker data-id="element-1338" data-mp="3pmxjorfc8mns">Who it is for</Kicker>
            <p className="mt-4 text-[17px] text-body" data-id="element-1339" data-mp="3km9n7q8cbdm6">{line.who}</p>
          </div>
          <div data-id="element-1340" data-mp="3komckogfgq99">
            <Kicker data-id="element-1341" data-mp="3fmwyezxwrwgy">The outcome</Kicker>
            <p className="mt-4 text-[17px] text-body" data-id="element-1342" data-mp="3fnyan7qnup09">{line.outcome}</p>
          </div>
        </div>
      </Section>

      <Section tone="mist" aria-labelledby="sub-services" data-id="element-1343" data-mp="3bn63ck1cmu6m">
        <SectionHeader id="sub-services" kicker="What we do" title="Four areas of work" data-id="element-1344" data-mp="c6zp0t7kmsjo" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2" data-id="element-1345" data-mp="76v3upvpx9n4">
          {line.subServices.map((sub, index) => <Reveal key={sub.name} delay={index * 0.05} data-id="element-1346" data-mp="2fv8hp28yzmq" data-mpi={index}>
              <Card className="h-full" data-id="element-1347" data-mp="2ead5x83zcnt">
                <h3 className="font-display text-[21px] leading-snug text-ink" data-id="element-1348" data-mp="3ul8vv8t3v996">{sub.name}</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-1349" data-mp="uxh969ec8ftf">{sub.detail}</p>
              </Card>
            </Reveal>)}
        </div>
      </Section>

      <Section aria-labelledby="use-cases" data-id="element-1350" data-mp="q44q0esfh182">
        <SectionHeader id="use-cases" kicker="In practice" title="Examples by sector" lead="Anonymised until each client agrees to be named. The numbers are theirs, not ours." data-id="element-1351" data-mp="28tr1ujkjfssr" />
        <div className="mt-10 divide-y divide-line border-y border-line" data-id="element-1352" data-mp="2hsw1jt7u5s6i">
          {line.useCases.map(useCase => <div key={useCase.sector} className="grid gap-3 py-6 sm:grid-cols-[180px_1fr]" data-id="element-1353" data-mp="2mu1r5i678zbt">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-1354" data-mp="2rrl7cnmgjm6e">{useCase.sector}</p>
              <p className="text-[16px] text-body" data-id="element-1355" data-mp="2wotpwpbips86">{useCase.outcome}</p>
            </div>)}
        </div>
      </Section>

      <Section tone="champagne" aria-labelledby="packages" data-id="element-1356" data-mp="35izzpl298zq4">
        <SectionHeader id="packages" kicker="Engagements" title={`Working with ${line.name}`} lead="Three ways this line is usually scoped. Every engagement begins with a discovery call and a written proposal, agreed before any work starts." data-id="element-1357" data-mp="35lci1zbxdc3p" />
        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-1358" data-mp="31svzqya8sta9">
          {line.packages.map(option => <Card key={option.name} className="flex h-full flex-col" data-id="element-1359" data-mp="2wvcc57rnn45n">
              <h3 className="font-display text-[22px] text-ink" data-id="element-1360" data-mp="3bj72ihi6wwfk">{option.name}</h3>
              <p className="mt-3 text-[15px] text-body" data-id="element-1361" data-mp="36l5j1vguqv3k">{option.summary}</p>
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1362" data-mp="2ivl03ckp7xua">
                What it includes
              </p>
              <ul className="mt-4 space-y-3 text-[14px] text-body" data-id="element-1363" data-mp="2dyb1b6bf69ts">
                {option.includes.map(item => <li key={item} className="flex gap-3" data-id="element-1364" data-mp="2srxr22ouzxlc">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1365" data-mp="2ntw95l3664bg" />
                    {item}
                  </li>)}
              </ul>
              <div className="mt-auto pt-7" data-id="element-1366" data-mp="3588vmioxn58">
                <Button to="/contact" variant="secondary" className="w-full" data-id="element-1367" data-mp="34fab8tvy3y6">
                  Discuss this engagement
                </Button>
              </div>
            </Card>)}
        </div>
        <Callout className="mt-8" data-id="element-1368" data-mp="fl7wdxjfy7d2">
          Scope, timing and fees are set out in a written proposal after a discovery call. Nothing on this page is
          financial or legal advice, and Cavalla Solution is not a financial adviser.
        </Callout>
      </Section>

      {related.length > 0 && <Section aria-labelledby="related" data-id="element-1369" data-mp="tgsfasz66pkh">
          <SectionHeader id="related" kicker="Related reading" title={`Insights on ${line.short}`} data-id="element-1370" data-mp="ye22e1z36p55" />
          <div className="mt-10 grid gap-8 sm:grid-cols-3" data-id="element-1371" data-mp="kn723e2nlgd6">
            {related.map(article => <article key={article.slug} data-id="element-1372" data-mp="on7ekt66zhhl">
                <p className="text-small uppercase tracking-[0.12em] text-gold" data-id="element-1373" data-mp="olwccxseahsl">{article.sectors[0]}</p>
                <h3 className="mt-3 font-display text-[19px] leading-snug text-ink" data-id="element-1374" data-mp="1cbgwvl475n3z">
                  <Link to={`/insights/${article.slug}`} className="underline-offset-4 hover:underline" data-id="element-1375" data-mp="1h5l5e8t42376">
                    {article.title}
                  </Link>
                </h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-1376" data-mp="1mz6s84kjk7jz">{article.excerpt}</p>
              </article>)}
          </div>
        </Section>}
    </>;
}